import Log from '../Utility/Log';

/**
 * Manages JSON-LD structured data injection for SEO optimization
 *
 * @author Matt Kenefick <matt@polymermallard.com>
 * @package Manager
 * @project ChalkySticks
 */
export class JsonLd {
	/**
	 * Collection of JSON-LD data blocks waiting to be injected
	 *
	 * @type Array<Record<string, any>>
	 */
	private dataBlocks: Array<Record<string, any>> = [];

	/**
	 * Map of injected script element IDs to prevent duplicates
	 *
	 * @type Set<string>
	 */
	private injectedIds: Set<string> = new Set();

	/**
	 * Add a business/venue data block for injection
	 *
	 * @param Record<string, any> venueData
	 * @param string identifier
	 * @return void
	 */
	public addBusiness(venueData: Record<string, any>, identifier: string = ''): void {
		const businessData = this.createBusinessStructuredData(venueData);

		if (businessData) {
			this.addDataBlock(businessData, identifier || `business-${venueData.id || Date.now()}`);
		}
	}

	/**
	 * Add multiple businesses from a collection
	 *
	 * @param Array<Record<string, any>> venues
	 * @return void
	 */
	public addBusinessCollection(venues: Array<Record<string, any>>): void {
		venues.forEach((venue) => {
			this.addBusiness(venue);
		});
	}

	/**
	 * Add a generic JSON-LD data block
	 *
	 * @param Record<string, any> data
	 * @param string identifier
	 * @return void
	 */
	public addDataBlock(data: Record<string, any>, identifier: string = ''): void {
		if (!data || typeof data !== 'object') {
			Log.debug('jsonld', '🔺 Invalid JSON-LD data provided');
			return;
		}

		const id = identifier || `jsonld-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

		// Check if this ID was already injected
		if (this.injectedIds.has(id)) {
			Log.debug('jsonld', `🔺 JSON-LD block with ID "${id}" already exists`);
			return;
		}

		this.dataBlocks.push({
			...data,
			_identifier: id,
		});

		Log.debug('jsonld', `✅ Added JSON-LD block: ${id}`);
	}

	/**
	 * Clear all pending data blocks
	 *
	 * @return void
	 */
	public clearDataBlocks(): void {
		this.dataBlocks = [];
		Log.debug('jsonld', '🧹 Cleared all pending JSON-LD blocks');
	}

	/**
	 * Create LocalBusiness structured data from venue information
	 *
	 * @param Record<string, any> venueData
	 * @return Record<string, any> | null
	 */
	public createBusinessStructuredData(venueData: Record<string, any>): Record<string, any> | null {
		if (!venueData.name) {
			Log.debug('jsonld', '🔺 Venue missing required name field');
			return null;
		}

		const structuredData: Record<string, any> = {
			'@context': 'https://schema.org',
			'@type': 'BarOrPub', // 'LocalBusiness',
			'name': venueData.name,
		};

		// Add optional fields if available
		if (venueData.description) {
			structuredData.description = venueData.description;
		}

		if (venueData.address) {
			structuredData.address = {
				'@type': 'PostalAddress',
				'addressCountry': venueData.address.country || venueData.country || 'US',
				'addressLocality': venueData.address.city || venueData.city,
				'addressRegion': venueData.address.state || venueData.state,
				'postalCode': venueData.address.zip || venueData.zip,
				'streetAddress': venueData.address.street || venueData.address,
			};
		}

		if (venueData.phone) {
			structuredData.telephone = venueData.phone;
		}

		if (venueData.website || venueData.url) {
			structuredData.url = venueData.website || venueData.url;
		}

		if (venueData.latitude && venueData.longitude) {
			structuredData.geo = {
				'@type': 'GeoCoordinates',
				'latitude': venueData.latitude,
				'longitude': venueData.longitude,
			};
		}

		// Add opening hours if available
		if (venueData.hours || venueData.openingHours) {
			structuredData.openingHours = this.formatOpeningHours(venueData.hours || venueData.openingHours);
		}

		// Add images if available
		if (venueData.images && Array.isArray(venueData.images) && venueData.images.length > 0) {
			structuredData.image = venueData.images
				.map((img: any) => {
					return typeof img === 'string' ? img : img.url || img.src;
				})
				.filter(Boolean);
		} else if (venueData.image) {
			structuredData.image = [venueData.image];
		}

		// Add pool/billiards specific categories
		if (venueData.type === 'pool_hall' || venueData.categories?.includes('pool') || venueData.categories?.includes('billiards')) {
			structuredData.additionalType = 'https://schema.org/SportsActivityLocation';
			structuredData.sport = ['Pool', 'Billiards'];
		}

		const useRating = false;

		// Add rating if available
		if (useRating && venueData.rating) {
			structuredData.aggregateRating = {
				'@type': 'AggregateRating',
				'bestRating': venueData.maxRating || 5,
				'ratingValue': venueData.rating,
			};

			if (venueData.reviewCount) {
				structuredData.aggregateRating.ratingCount = venueData.reviewCount;
			}
		}

		// Add price range if available
		if (venueData.priceRange) {
			structuredData.priceRange = venueData.priceRange;
		}

		return structuredData;
	}

	/**
	 * Format opening hours for schema.org format
	 *
	 * @param any hoursData
	 * @return Array<string>
	 */
	private formatOpeningHours(hoursData: any): Array<string> {
		if (typeof hoursData === 'string') {
			return [hoursData];
		}

		if (Array.isArray(hoursData)) {
			return hoursData;
		}

		if (typeof hoursData === 'object') {
			const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
			const formatted: Array<string> = [];

			days.forEach((day) => {
				const dayKey = day.toLowerCase();
				if (hoursData[dayKey]) {
					formatted.push(`${day.substr(0, 2)} ${hoursData[dayKey]}`);
				}
			});

			return formatted;
		}

		return [];
	}

	/**
	 * Inject all pending JSON-LD blocks into the DOM
	 *
	 * @return Promise<void>
	 */
	public async injectAll(): Promise<void> {
		if (this.dataBlocks.length === 0) {
			Log.debug('jsonld', '📭 No JSON-LD blocks to inject');
			return;
		}

		const injectedCount = this.dataBlocks.length;

		this.dataBlocks.forEach((dataBlock) => {
			this.injectSingle(dataBlock);
		});

		// Clear the blocks after injection
		this.dataBlocks = [];

		Log.debug('jsonld', `🚀 Injected ${injectedCount} JSON-LD blocks`);
	}

	/**
	 * Inject a single JSON-LD block into the DOM
	 *
	 * @param Record<string, any> data
	 * @return void
	 */
	private injectSingle(data: Record<string, any>): void {
		if (typeof document === 'undefined') {
			Log.debug('jsonld', '🔺 Document not available (SSR context)');
			return;
		}

		const identifier = data._identifier;
		delete data._identifier; // Remove internal identifier

		// Create script element
		const script = document.createElement('script');
		script.type = 'application/ld+json';
		script.id = `jsonld-${identifier}`;
		script.textContent = JSON.stringify(data, null, 0);

		// Inject into head
		document.head.appendChild(script);

		// Track injection
		this.injectedIds.add(identifier);

		Log.debug('jsonld', `✅ Injected JSON-LD: ${identifier}`);
	}

	/**
	 * Remove all injected JSON-LD scripts from DOM
	 *
	 * @return void
	 */
	public removeAll(): void {
		if (typeof document === 'undefined') {
			return;
		}

		this.injectedIds.forEach((id) => {
			const element = document.getElementById(`jsonld-${id}`);
			if (element) {
				element.remove();
			}
		});

		this.injectedIds.clear();
		Log.debug('jsonld', '🧹 Removed all JSON-LD scripts from DOM');
	}

	/**
	 * Remove a specific JSON-LD script by identifier
	 *
	 * @param string identifier
	 * @return void
	 */
	public removeById(identifier: string): void {
		if (typeof document === 'undefined') {
			return;
		}

		const element = document.getElementById(`jsonld-${identifier}`);
		if (element) {
			element.remove();
			this.injectedIds.delete(identifier);
			Log.debug('jsonld', `🗑️ Removed JSON-LD: ${identifier}`);
		}
	}
}

// Singleton
export default new JsonLd();
